import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import { Navigation } from '../components/Navigation';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';

const MONTHLY_TARGET = 8000000;
const DB_MIN_PCT = 63;

const EXPENSE_BUDGETS = [
  { key: 'loenninger', label: 'Lønninger', budget: 26, color: '#EF4444' },
  { key: 'farveproever', label: 'Farveprøver', budget: 0.1, color: '#F59E0B' },
  { key: 'rejse_moede', label: 'Rejse- og mødeomk.', budget: 0.6, color: '#10B981' },
  { key: 'salgsomkostninger', label: 'Salgsomkostninger', budget: 20, color: '#3B82F6' },
  { key: 'vaerktoj_udstyr', label: 'Værktøj og udstyr', budget: 0.5, color: '#A855F7' },
  { key: 'autodrift', label: 'Autodrift', budget: 3.5, color: '#8B5CF6' },
  { key: 'lokaleomkostninger', label: 'Lokaleomkostninger', budget: 0.1, color: '#EC4899' },
  { key: 'software', label: 'Software', budget: 0.1, color: '#06B6D4' },
  { key: 'admin_omk', label: 'Adm. omkostninger', budget: 0.4, color: '#F97316' },
];

const formatDKK = (v) => {
  if (v >= 1000000) return `${(v / 1000000).toFixed(2)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(0)}k`;
  return v?.toFixed(0) || '0';
};

const formatHours = (v) => {
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return Math.round(v).toLocaleString('da-DK');
};

// Timer Gauge component - similar to DGGauge but for hours coverage
function TimerGauge({ usedHours, neededHours, remainingHours }) {
  const size = 200;
  const radius = (size - 20) / 2;
  const cx = size / 2;
  const cy = size / 2 + 10;
  const startAngle = Math.PI;
  const endAngle = 0;

  const totalAvailable = usedHours + remainingHours;
  const coveragePct = neededHours > 0 ? Math.min((totalAvailable / neededHours) * 100, 100) : 0;
  const usedPct = neededHours > 0 ? Math.min((usedHours / neededHours) * 100, 100) : 0;
  
  const usedAngle = startAngle - (usedPct / 100) * (startAngle - endAngle);
  const coverageAngle = startAngle - (coveragePct / 100) * (startAngle - endAngle);

  const arcPath = (start, end, r) => {
    const x1 = cx + r * Math.cos(start);
    const y1 = cy - r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy - r * Math.sin(end);
    const largeArc = start - end > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const isEnough = totalAvailable >= neededHours;
  const surplus = totalAvailable - neededHours;

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size / 2 + 35} viewBox={`0 0 ${size} ${size / 2 + 35}`}>
        {/* Background arc */}
        <path d={arcPath(startAngle, endAngle, radius)} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="14" strokeLinecap="round" />
        {/* Remaining hours arc (blue zone) */}
        {remainingHours > 0 && coveragePct > usedPct && (
          <path d={arcPath(usedAngle, coverageAngle, radius)} fill="none" stroke="rgba(59,130,246,0.4)" strokeWidth="14" strokeLinecap="round" />
        )}
        {/* Used hours arc (green/red) */}
        {usedPct > 0 && (
          <path d={arcPath(startAngle, usedAngle, radius)} fill="none" stroke={isEnough ? '#22C55E' : '#F59E0B'} strokeWidth="14" strokeLinecap="round" />
        )}
        {/* Needle at coverage point */}
        <circle cx={cx} cy={cy} r="5" fill="#fff" />
        {/* Value text */}
        <text x={cx} y={cy - 16} textAnchor="middle" fill={isEnough ? '#22C55E' : '#EF4444'} fontSize="24" fontWeight="800">{coveragePct.toFixed(0)}%</text>
        <text x={cx} y={cy + 2} textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="10">Timer-dækning</text>
      </svg>
      <div className={`mt-1 px-3 py-1 rounded-full text-xs font-semibold ${isEnough ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
        {isEnough ? `+${formatHours(surplus)} timer overskud` : `${formatHours(Math.abs(surplus))} timer mangler`}
      </div>
    </div>
  );
}

// Gauge component for DG%
function DGGauge({ value, minTarget }) {
  const size = 220;
  const radius = (size - 20) / 2;
  const cx = size / 2;
  const cy = size / 2 + 10;
  const startAngle = Math.PI;
  const endAngle = 0;

  const clampedValue = Math.min(Math.max(value, 0), 100);
  const valueAngle = startAngle - (clampedValue / 100) * (startAngle - endAngle);
  const targetAngle = startAngle - (minTarget / 100) * (startAngle - endAngle);

  const arcPath = (start, end, r) => {
    const x1 = cx + r * Math.cos(start);
    const y1 = cy - r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy - r * Math.sin(end);
    const largeArc = start - end > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const isOk = value >= minTarget;
  const gaugeColor = isOk ? '#22C55E' : '#EF4444';
  const needleX = cx + (radius - 14) * Math.cos(valueAngle);
  const needleY = cy - (radius - 14) * Math.sin(valueAngle);
  const targetX = cx + (radius + 6) * Math.cos(targetAngle);
  const targetY = cy - (radius + 6) * Math.sin(targetAngle);
  const targetInnerX = cx + (radius - 18) * Math.cos(targetAngle);
  const targetInnerY = cy - (radius - 18) * Math.sin(targetAngle);

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size / 2 + 35} viewBox={`0 0 ${size} ${size / 2 + 35}`}>
        <path d={arcPath(startAngle, endAngle, radius)} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="14" strokeLinecap="round" />
        {/* Red zone: 0 to minTarget */}
        <path d={arcPath(startAngle, targetAngle, radius)} fill="none" stroke="rgba(239,68,68,0.2)" strokeWidth="14" strokeLinecap="round" />
        {/* Green zone: minTarget to 100% */}
        <path d={arcPath(targetAngle, endAngle, radius)} fill="none" stroke="rgba(34,197,94,0.15)" strokeWidth="14" strokeLinecap="round" />
        {/* Value arc */}
        {clampedValue > 0 && (
          <path d={arcPath(startAngle, valueAngle, radius)} fill="none" stroke={gaugeColor} strokeWidth="14" strokeLinecap="round" />
        )}
        {/* Target marker line */}
        <line x1={targetInnerX} y1={targetInnerY} x2={targetX} y2={targetY} stroke="#F59E0B" strokeWidth="2.5" />
        <text x={targetX + (targetAngle > Math.PI / 2 ? -8 : 8)} y={targetY - 6} textAnchor="middle" fill="#F59E0B" fontSize="10" fontWeight="600">{minTarget}%</text>
        {/* Needle */}
        <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke="#fff" strokeWidth="2.5" />
        <circle cx={cx} cy={cy} r="4" fill="#fff" />
        {/* Value text */}
        <text x={cx} y={cy - 16} textAnchor="middle" fill={gaugeColor} fontSize="28" fontWeight="800">{value.toFixed(1)}%</text>
        <text x={cx} y={cy + 2} textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="11">Dækningsgrad</text>
      </svg>
      <div className={`mt-1 px-3 py-1 rounded-full text-xs font-semibold ${isOk ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
        {isOk ? `${(value - minTarget).toFixed(1)}% over minimum` : `${(minTarget - value).toFixed(1)}% under minimum`}
      </div>
    </div>
  );
}

export default function MaanedsAnalyse() {
  const [data, setData] = useState(null);
  const [saldoRevenue, setSaldoRevenue] = useState(null);
  const [saldoRawData, setSaldoRawData] = useState([]);
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orderRes, saldoRes, bookingRes] = await Promise.all([
          api.get('/orders/monthly-summary'),
          api.get('/saldobalance/omsaetning').catch(() => null),
          api.get('/bookings/monthly-booking-target').catch(() => null),
        ]);
        setData(orderRes.data);
        if (bookingRes?.data && !bookingRes.data.error) {
          setBookingData(bookingRes.data);
        }

        if (saldoRes?.data?.data) {
          const allData = saldoRes.data.data.filter(d => d.omsaetning > 0);
          setSaldoRawData(allData);
          const lastWithData = [...allData].reverse().find(m => m.omsaetning > 0);
          if (lastWithData) {
            setSaldoRevenue(lastWithData);
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const analysis = useMemo(() => {
    if (!data) return null;

    const now = new Date();
    const daysInMonth = new Date(data.year, data.month, 0).getDate();
    const currentDay = data.year === now.getFullYear() && data.month === (now.getMonth() + 1)
      ? now.getDate() : daysInMonth;

    const totalRevenue = saldoRevenue?.omsaetning || data.summary?.total_revenue || 0;
    const progressPct = (totalRevenue / MONTHLY_TARGET) * 100;
    const expectedPct = (currentDay / daysInMonth) * 100;
    const expectedRevenue = (currentDay / daysInMonth) * MONTHLY_TARGET;
    const diff = totalRevenue - expectedRevenue;
    const isAhead = diff >= 0;
    const dailyAvg = currentDay > 0 ? totalRevenue / currentDay : 0;
    const projectedTotal = dailyAvg * daysInMonth;
    const willReachTarget = projectedTotal >= MONTHLY_TARGET;
    const daysRemaining = Math.max(daysInMonth - currentDay + 1, 0);
    const neededPerDay = daysRemaining > 0 ? (MONTHLY_TARGET - totalRevenue) / daysRemaining : 0;

    // Build chart data
    const chartData = [];
    const dailyMap = {};
    (data.daily_data || []).forEach(d => { dailyMap[d.day_of_month] = d; });
    const orderTotal = data.summary?.total_revenue || 0;
    const scaleFactor = (orderTotal > 0 && totalRevenue !== orderTotal) ? totalRevenue / orderTotal : 1;

    let lastAccum = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const targetLine = (day / daysInMonth) * MONTHLY_TARGET;
      const projectionLine = dailyAvg * day;
      const entry = { dag: day, maal: Math.round(targetLine) };
      if (day <= currentDay) {
        if (dailyMap[day]) lastAccum = dailyMap[day].akkumuleret_omsaetning;
        entry.omsaetning = Math.round(lastAccum * scaleFactor);
        entry.projektion = Math.round(projectionLine);
      } else {
        entry.omsaetning = null;
        entry.projektion = Math.round(projectionLine);
      }
      chartData.push(entry);
    }

    return {
      totalRevenue, progressPct, expectedPct, diff, isAhead,
      dailyAvg, projectedTotal, willReachTarget, daysRemaining, neededPerDay,
      currentDay, daysInMonth, chartData,
      monthName: data.month_name, year: data.year, totalOrders: data.summary?.total_orders || 0,
    };
  }, [data, saldoRevenue]);

  // DB and expense analysis from saldo data (same source as Økonomisk Analyse)
  const finAnalysis = useMemo(() => {
    if (!saldoRevenue) return null;
    const d = saldoRevenue;
    const oms = d.omsaetning || 0;
    const db = d.daekningsbidrag || 0;
    const dbPct = oms > 0 ? (db / oms) * 100 : 0;

    const expenses = EXPENSE_BUDGETS.map(exp => {
      const actual = d[exp.key] || 0;
      const actualPct = oms > 0 ? (actual / oms) * 100 : 0;
      const overBudget = actualPct > exp.budget;
      const diff = actualPct - exp.budget;
      return { ...exp, actual, actualPct, overBudget, diff };
    });

    const totalExpenseBudget = EXPENSE_BUDGETS.reduce((s, e) => s + e.budget, 0);
    const totalExpenseActual = expenses.reduce((s, e) => s + e.actualPct, 0);

    return { dbPct, db, oms, expenses, totalExpenseBudget, totalExpenseActual };
  }, [saldoRevenue]);

  const NavHeader = () => (
    <header className="sticky top-0 z-50 bg-[#121212]/95 backdrop-blur-md border-b border-white/5">
      <div className="max-w-[1920px] mx-auto px-6 py-4">
        <div className="flex items-center gap-4">
          <Navigation />
          <h1 className="text-xl font-semibold text-white">Måneds Analyse</h1>
        </div>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10]">
        <NavHeader />
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#66FCF1] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen bg-[#0B0C10]">
        <NavHeader />
        <div className="text-center text-gray-400 py-20">Ingen data tilgængelig</div>
      </div>
    );
  }

  const {
    totalRevenue, progressPct, expectedPct, diff, isAhead,
    dailyAvg, projectedTotal, willReachTarget, daysRemaining,
    neededPerDay, currentDay, daysInMonth, chartData, monthName, year, totalOrders,
  } = analysis;

  const statusColor = willReachTarget ? '#22C55E' : '#EF4444';
  const statusBg = willReachTarget ? 'from-green-900/40 to-green-900/10' : 'from-red-900/40 to-red-900/10';
  const statusBorder = willReachTarget ? 'border-green-500/40' : 'border-red-500/40';

  return (
    <div className="min-h-screen bg-[#0B0C10]">
      <NavHeader />
      <main className="max-w-[1920px] mx-auto px-6 py-8">
        <div className="space-y-6" data-testid="maaneds-analyse-page">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-1">Måneds Analyse</h2>
            <p className="text-gray-400">{monthName} {year} — Mål: {formatDKK(MONTHLY_TARGET)} kr</p>
          </div>

          {/* Status banner */}
          <div className={`bg-gradient-to-r ${statusBg} rounded-xl p-5 border ${statusBorder}`} data-testid="status-banner">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 rounded-full animate-pulse" style={{ backgroundColor: statusColor }} />
                <div>
                  <div className="text-white font-bold text-lg">
                    {willReachTarget ? 'På vej mod målet' : 'Risiko for ikke at nå målet'}
                  </div>
                  <div className="text-gray-300 text-sm">
                    {isAhead ? `${formatDKK(Math.abs(diff))} kr foran plan` : `${formatDKK(Math.abs(diff))} kr bagud plan`}
                    {' '}— Dag {currentDay} af {daysInMonth}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ color: statusColor }}>
                  {formatDKK(projectedTotal)} kr
                </div>
                <div className="text-gray-400 text-xs">Projekteret slutresultat</div>
              </div>
            </div>
          </div>

          {/* Main gauge / progress */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6" data-testid="progress-section">
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="text-gray-400 text-sm">Omsætning MTD</div>
                <div className="text-3xl font-bold text-white">{formatDKK(totalRevenue)} kr</div>
              </div>
              <div className="text-right">
                <div className="text-gray-400 text-sm">Mål</div>
                <div className="text-xl font-semibold text-white">{formatDKK(MONTHLY_TARGET)} kr</div>
              </div>
            </div>
            <div className="relative h-10 bg-gray-900 rounded-full overflow-hidden border border-gray-700">
              <div className="absolute top-0 bottom-0 w-0.5 bg-yellow-400 z-20" style={{ left: `${Math.min(expectedPct, 100)}%` }} />
              <div className="absolute -top-5 text-[10px] text-yellow-400 font-medium z-20 whitespace-nowrap" style={{ left: `${Math.min(expectedPct, 100)}%`, transform: 'translateX(-50%)' }}>
                Forventet
              </div>
              <div className="absolute top-0 bottom-0 rounded-full transition-all duration-1000 ease-out" style={{
                width: `${Math.min(progressPct, 100)}%`,
                background: progressPct >= expectedPct ? 'linear-gradient(90deg, #059669, #22C55E)' : 'linear-gradient(90deg, #DC2626, #EF4444)',
              }} />
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <span className="text-white font-bold text-sm drop-shadow-lg">{progressPct.toFixed(1)}%</span>
              </div>
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>0 kr</span>
              <span>{formatDKK(MONTHLY_TARGET)} kr</span>
            </div>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Dagligt gennemsnit', value: `${formatDKK(dailyAvg)} kr`, sub: `${totalOrders} ordrer i alt` },
              { label: 'Projekteret total', value: `${formatDKK(projectedTotal)} kr`, sub: willReachTarget ? 'Over mål' : 'Under mål', color: statusColor },
              { label: 'Mangler', value: `${formatDKK(Math.max(MONTHLY_TARGET - totalRevenue, 0))} kr`, sub: `${daysRemaining} dage tilbage` },
              { label: 'Krævet pr. dag', value: `${formatDKK(Math.max(neededPerDay, 0))} kr`, sub: neededPerDay > dailyAvg ? 'Over gennemsnit' : 'Under gennemsnit', color: neededPerDay > dailyAvg ? '#EF4444' : '#22C55E' },
            ].map((card, i) => (
              <div key={i} className="bg-gray-800/50 rounded-xl border border-gray-700 p-4" data-testid={`kpi-card-${i}`}>
                <div className="text-gray-400 text-xs mb-1">{card.label}</div>
                <div className="text-white font-bold text-lg">{card.value}</div>
                <div className="text-xs mt-1" style={{ color: card.color || '#9CA3AF' }}>{card.sub}</div>
              </div>
            ))}
          </div>

          {/* === 3-COLUMN CHART GRID === */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Chart 1: Accumulated revenue vs target line */}
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6" data-testid="revenue-chart">
              <h3 className="text-white font-semibold mb-4">Akkumuleret omsætning vs. mål</h3>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                    <defs>
                      <linearGradient id="gradOms" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={statusColor} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={statusColor} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradProj" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366F1" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="dag" stroke="rgba(255,255,255,0.3)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} tickFormatter={(v) => v % 7 === 0 || v === 1 ? v : ''} />
                    <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} tickFormatter={(v) => formatDKK(v)} width={50} />
                    <Tooltip contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px' }} labelFormatter={(v) => `Dag ${v}`} formatter={(value, name) => {
                      if (value === null) return ['-', name];
                      const labels = { omsaetning: 'Omsætning', maal: 'Mål-linje', projektion: 'Projektion' };
                      return [`${formatDKK(value)} kr`, labels[name] || name];
                    }} />
                    <ReferenceLine y={MONTHLY_TARGET} stroke="#F59E0B" strokeDasharray="6 4" strokeWidth={2} />
                    <Area type="monotone" dataKey="maal" stroke="#F59E0B" strokeWidth={2} strokeDasharray="4 4" fill="none" dot={false} name="maal" />
                    <Area type="monotone" dataKey="projektion" stroke="#6366F1" strokeWidth={1.5} strokeDasharray="3 3" fill="url(#gradProj)" dot={false} name="projektion" />
                    <Area type="monotone" dataKey="omsaetning" stroke={statusColor} strokeWidth={2.5} fill="url(#gradOms)" dot={false} activeDot={{ r: 4, fill: statusColor }} name="omsaetning" connectNulls={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-3 justify-center mt-3 text-[10px]">
                <div className="flex items-center gap-1"><div className="w-3 h-0.5 rounded" style={{ backgroundColor: statusColor }} /><span className="text-gray-400">Omsætning</span></div>
                <div className="flex items-center gap-1"><div className="w-3 h-0.5 rounded bg-yellow-500" /><span className="text-gray-400">Mål</span></div>
                <div className="flex items-center gap-1"><div className="w-3 h-0.5 rounded bg-indigo-500" /><span className="text-gray-400">Projektion</span></div>
              </div>
            </div>

            {/* Chart 2: Booking chart */}
            {bookingData && (
              <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6" data-testid="booking-chart">
                <h3 className="text-white font-semibold mb-4">Akkumulerede bookinger vs. mål</h3>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={bookingData.chart_data} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                      <defs>
                        <linearGradient id="gradBooking" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={bookingData.will_reach_target ? '#22C55E' : '#EF4444'} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={bookingData.will_reach_target ? '#22C55E' : '#EF4444'} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="dag" stroke="rgba(255,255,255,0.3)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} tickFormatter={(v) => v % 7 === 0 || v === 1 ? v : ''} />
                      <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} width={40} />
                      <Tooltip contentStyle={{ backgroundColor: '#222', border: '1px solid #555', borderRadius: '10px', color: '#fff' }} labelStyle={{ color: '#fff', fontWeight: 'bold' }} itemStyle={{ color: '#fff' }} labelFormatter={(v) => `Dag ${v}`} formatter={(value, name) => {
                        if (value === null) return ['-', name];
                        const labels = { afviklede: 'Afviklede', maal: 'Mål-linje', projektion: 'Projektion' };
                        return [value, labels[name] || name];
                      }} />
                      <ReferenceLine y={bookingData.bookings_needed_total} stroke="#F59E0B" strokeDasharray="6 4" strokeWidth={2} />
                      <Area type="monotone" dataKey="maal" stroke="#F59E0B" strokeWidth={2} strokeDasharray="4 4" fill="none" dot={false} name="maal" />
                      <Area type="monotone" dataKey="projektion" stroke="#6366F1" strokeWidth={1.5} strokeDasharray="3 3" fill="none" dot={false} name="projektion" />
                      <Area type="monotone" dataKey="afviklede" stroke={bookingData.will_reach_target ? '#22C55E' : '#EF4444'} strokeWidth={2.5} fill="url(#gradBooking)" dot={false} activeDot={{ r: 4 }} name="afviklede" connectNulls={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-3 justify-center mt-3 text-[10px]">
                  <div className="flex items-center gap-1"><div className="w-3 h-0.5 rounded" style={{ backgroundColor: bookingData.will_reach_target ? '#22C55E' : '#EF4444' }} /><span className="text-gray-400">Afviklede</span></div>
                  <div className="flex items-center gap-1"><div className="w-3 h-0.5 rounded bg-yellow-500" /><span className="text-gray-400">Mål ({bookingData.bookings_needed_total})</span></div>
                  <div className="flex items-center gap-1"><div className="w-3 h-0.5 rounded bg-indigo-500" /><span className="text-gray-400">Projektion</span></div>
                </div>
              </div>
            )}

            {/* Chart 3: Dækningsgrad Gauge */}
            {finAnalysis && (
              <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6" data-testid="dg-gauge">
                <h3 className="text-white font-semibold mb-2 text-center">Dækningsgrad</h3>
                <p className="text-gray-500 text-xs text-center mb-4">Minimum: {DB_MIN_PCT}%</p>
                <DGGauge value={finAnalysis.dbPct} minTarget={DB_MIN_PCT} />
                <div className="mt-4 grid grid-cols-2 gap-4 max-w-xs mx-auto">
                  <div className="text-center">
                    <p className="text-white/40 text-xs">Omsætning</p>
                    <p className="text-white text-sm font-semibold">{formatDKK(Math.round(finAnalysis.oms))} kr</p>
                  </div>
                  <div className="text-center">
                    <p className="text-white/40 text-xs">Dækningsbidrag</p>
                    <p className="text-sm font-semibold" style={{ color: finAnalysis.dbPct >= DB_MIN_PCT ? '#22C55E' : '#EF4444' }}>{formatDKK(Math.round(finAnalysis.db))} kr</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* === ROW 2: Booking + Timer progress bars === */}
          {bookingData && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Booking progress bar */}
              <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6" data-testid="booking-progress">
                <h3 className="text-white font-semibold mb-3">Booking Status</h3>
                <div className="flex items-end justify-between mb-2">
                  <div>
                    <div className="text-gray-400 text-xs">Afviklede</div>
                    <div className="text-2xl font-bold text-white">{bookingData.afviklede_total}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-gray-400 text-xs">Mål</div>
                    <div className="text-lg font-semibold text-white">{bookingData.bookings_needed_total}</div>
                  </div>
                </div>
                <div className="relative h-10 bg-gray-900 rounded-lg overflow-hidden border border-gray-700">
                  <div className="absolute top-0 bottom-0 transition-all duration-1000" style={{
                    width: `${Math.min((bookingData.afviklede_total / bookingData.bookings_needed_total) * 100, 100)}%`,
                    background: 'linear-gradient(90deg, #059669, #22C55E)',
                  }} />
                  {bookingData.remaining_in_calendar > 0 && (
                    <div className="absolute top-0 bottom-0 transition-all duration-1000" style={{
                      left: `${Math.min((bookingData.afviklede_total / bookingData.bookings_needed_total) * 100, 100)}%`,
                      width: `${Math.min((bookingData.remaining_in_calendar / bookingData.bookings_needed_total) * 100, 100 - (bookingData.afviklede_total / bookingData.bookings_needed_total) * 100)}%`,
                      background: 'linear-gradient(90deg, #2563EB, #3B82F6)',
                    }} />
                  )}
                  <div className="absolute inset-0 flex items-center px-2 z-10">
                    <span className="text-white text-[10px] font-bold drop-shadow-lg">{bookingData.afviklede_total}</span>
                    {bookingData.remaining_in_calendar > 0 && (
                      <span className="text-white text-[10px] font-bold drop-shadow-lg ml-auto">+{bookingData.remaining_in_calendar}</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mt-2 text-[10px]">
                  <div className="flex items-center gap-1"><div className="w-2 h-2 rounded bg-green-500" /><span className="text-gray-400">Afviklede</span></div>
                  <div className="flex items-center gap-1"><div className="w-2 h-2 rounded bg-blue-500" /><span className="text-gray-400">I kalender</span></div>
                </div>
              </div>

              {/* Timer Gauge */}
              <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6" data-testid="timer-gauge">
                <h3 className="text-white font-semibold mb-2 text-center">Konsulent Timer</h3>
                <p className="text-gray-500 text-[10px] text-center mb-2">KPT: {bookingData.kpt_target}</p>
                <TimerGauge 
                  usedHours={bookingData.hours_used} 
                  neededHours={bookingData.hours_needed_total} 
                  remainingHours={bookingData.hours_remaining}
                />
                <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                  <div>
                    <p className="text-white/40 text-[10px]">Krævet</p>
                    <p className="text-white text-xs font-semibold">{formatHours(bookingData.hours_needed_total)}</p>
                  </div>
                  <div>
                    <p className="text-white/40 text-[10px]">Tilgængelig</p>
                    <p className="text-xs font-semibold" style={{ color: (bookingData.hours_used + bookingData.hours_remaining) >= bookingData.hours_needed_total ? '#22C55E' : '#EF4444' }}>
                      {formatHours(bookingData.hours_used + bookingData.hours_remaining)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Expense overview bar chart */}
              {finAnalysis && (
                <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6" data-testid="expense-overview">
                  <h3 className="text-white font-semibold mb-2 text-center">Udgifter vs. Budget</h3>
                  <p className="text-gray-500 text-[10px] text-center mb-2">
                    Budget: {finAnalysis.totalExpenseBudget.toFixed(1)}% — Aktuel: {finAnalysis.totalExpenseActual.toFixed(1)}%
                  </p>
                  <div className="h-[220px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={finAnalysis.expenses}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 5, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                        <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 9 }} tickFormatter={v => `${v}%`} />
                        <YAxis type="category" dataKey="label" tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 9 }} width={90} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#222', border: '1px solid #555', borderRadius: '10px', color: '#fff', fontSize: '11px' }}
                          labelStyle={{ color: '#fff', fontWeight: 'bold', marginBottom: '4px' }}
                          itemStyle={{ color: '#fff' }}
                          formatter={(value, name, props) => {
                            const item = props.payload;
                            return [`${value.toFixed(2)}% (budget: ${item.budget}%)`, 'Aktuel'];
                          }}
                        />
                        <Bar dataKey="actualPct" radius={[0, 4, 4, 0]} barSize={14}>
                          {finAnalysis.expenses.map((entry, i) => (
                            <Cell key={i} fill={entry.overBudget ? '#EF4444' : entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
